import { type ComponentProps, type FC, memo, useCallback } from 'react'
import { SelectableListMenu } from '@platform/ui'
import cn from 'classnames'
import type { SelectableListMenuDataWithMetadata } from '@/features/tocMenu/tocMenuSlice'
import { useNavigateToActivityPartByAxis } from '@/hooks/useActivityParts/useNavigateToActivityPartByAxis'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useAppDispatch } from '@/store/hooks'
import { setContentMenuState } from '@/store/slices/layoutSlice'
import { TOCMenuItem } from './components/TOCMenuItem/TOCMenuItem'
import { useTOCMenuItem } from './components/TOCMenuItem/useTOCMenuItem'
import styles from './TOCMenu.module.scss'

type SelectableListMenuProps = ComponentProps<typeof SelectableListMenu>

type Props = {
  tocItems: SelectableListMenuDataWithMetadata
  inlinedToComposition?: boolean
}

export const TOCMenu: FC<Props> = memo(({ tocItems, inlinedToComposition }) => {
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()

  const { navigateToActivityPartByAxis } = useNavigateToActivityPartByAxis()

  const properties = useTOCMenuItem()

  const handleClickMenuItem = useCallback(
    ({ element }: { element: SelectableListMenuDataWithMetadata }) => {
      if (!element.metadata) {
        console.warn('TOCMenu onClick has no metadata', element)
        return
      }

      const { isSelected } = properties({ activityPart: element.metadata.activityPart })

      if (isSelected) return

      if (isMobile) {
        dispatch(setContentMenuState(false))
      }

      navigateToActivityPartByAxis(element.metadata.activityPart.axis)
    },
    [properties, isMobile, navigateToActivityPartByAxis, dispatch],
  )

  const TocMenuItemRenderer = useCallback(
    ({
      element,
      dispatch,
    }: {
      element: SelectableListMenuDataWithMetadata
      dispatch: Parameters<SelectableListMenuProps['children']>[number]
    }) => {
      return <TOCMenuItem element={element} dispatch={dispatch} inlinedToComposition={inlinedToComposition} />
    },
    [inlinedToComposition],
  )

  return (
    <div className={cn(styles.wrapper, { [styles.inlinedToComposition]: inlinedToComposition })}>
      <SelectableListMenu
        onNodeSelect={handleClickMenuItem}
        data={tocItems}
        size="large"
        showTreeExpandIcon={!inlinedToComposition}
      >
        {TocMenuItemRenderer}
      </SelectableListMenu>
    </div>
  )
})
